import { useEffect, useState } from "react";
import { supabase } from "../../utils/supabaseClient";
import useRequireAdmin from "../../hooks/useRequireAdmin";
import AdminNav from "../../components/AdminNav";
import Pagination from "../../components/Pagination";

const PAGE_SIZE = 15;

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "amount_mismatch", label: "Amount mismatch" },
];

const FILTERS = [{ value: "all", label: "All" }, ...STATUSES];

const statusLabel = (value) =>
  STATUSES.find((s) => s.value === value)?.label ??
  (value ? value.charAt(0).toUpperCase() + value.slice(1) : "Unknown");

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
    : "";

const money = (n) => `£${Number(n).toFixed(2)}`;

function StatusBadge({ status }) {
  return <span className={`ui-badge ui-status--${status}`}>{statusLabel(status)}</span>;
}

// A dropdown that also copes with older statuses that are not in the list
function StatusSelect({ order, onChange, disabled, id }) {
  const known = STATUSES.some((s) => s.value === order.status);
  return (
    <select
      id={id}
      className="ui-select"
      style={{ width: "auto", minWidth: 150 }}
      value={order.status}
      disabled={disabled}
      aria-label={`Status for order ${order.id}`}
      onChange={(e) => onChange(order, e.target.value)}
    >
      {!known && <option value={order.status}>{statusLabel(order.status)}</option>}
      {STATUSES.map((s) => (
        <option key={s.value} value={s.value}>{s.label}</option>
      ))}
    </select>
  );
}

export default function AdminOrders() {
  const checking = useRequireAdmin();

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (checking) return;
    let cancelled = false;

    async function load() {
      setLoading(true);

      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase.from("orders").select("*", { count: "exact" });
      if (filter !== "all") query = query.eq("status", filter);

      const { data, count, error: queryError } = await query
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(from, to);

      if (cancelled) return;

      if (queryError) {
        if (queryError.code === "PGRST103" && page > 1) {
          setPage(1);
          return;
        }
        console.error(queryError);
        setError("We couldn't load the orders.");
        setOrders([]);
        setTotal(0);
      } else {
        setError("");
        setOrders(data ?? []);
        setTotal(count ?? 0);
      }
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [checking, page, filter, reloadKey]);

  // close the drawer with the Escape key
  useEffect(() => {
    if (!selectedOrder) return;
    const onKey = (e) => e.key === "Escape" && setSelectedOrder(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedOrder]);

  if (checking) {
    return (
      <div className="adm">
        <p className="adm-muted">Checking admin permissions...</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function changeFilter(value) {
    setFilter(value);
    setPage(1);
  }

  async function updateStatus(order, newStatus) {
    if (newStatus === order.status) return;

    setUpdatingId(order.id);
    setError("");

    const { error: updateError } = await supabase
      .from("orders")
      .update({ status: newStatus })
      .eq("id", order.id);

    setUpdatingId(null);

    if (updateError) {
      console.error(updateError);
      setError(`Could not update order #${order.id}: ${updateError.message}`);
      return;
    }

    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o)));
    setSelectedOrder((s) => (s && s.id === order.id ? { ...s, status: newStatus } : s));

    // the order no longer belongs in a filtered list
    if (filter !== "all" && filter !== newStatus) setReloadKey((k) => k + 1);
  }

  const items = (order) => (Array.isArray(order.items) ? order.items : []);

  return (
    <div className="adm">
      <div className="adm-header">
        <h2 className="adm-title">Orders</h2>
      </div>

      <AdminNav />

      <div className="adm-pills" role="group" aria-label="Filter orders by status">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`adm-pill${filter === f.value ? " is-active" : ""}`}
            aria-pressed={filter === f.value}
            onClick={() => changeFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="ui-alert ui-alert--error">{error}</p>}

      <section className="adm-card">
        {loading && orders.length === 0 ? (
          <p className="adm-empty">Loading orders...</p>
        ) : orders.length === 0 ? (
          <p className="adm-empty">
            {filter === "all" ? "No orders yet." : `No ${statusLabel(filter).toLowerCase()} orders.`}
          </p>
        ) : (
          <div className="adm-table-wrap" style={{ opacity: loading ? 0.6 : 1 }}>
            <table className="adm-table" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th className="adm-num">Total</th>
                  <th>Status</th>
                  <th>Change status</th>
                  <th><span className="sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td><strong>#{order.id}</strong></td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>
                      <div className="adm-ellipsis">{order.email}</div>
                      <small className="adm-muted">
                        {[order.first_name, order.last_name].filter(Boolean).join(" ")}
                      </small>
                    </td>
                    <td>
                      <div className="adm-ellipsis" title={items(order).map((i) => `${i.qty} x ${i.name}`).join(", ")}>
                        {items(order).map((i) => `${i.qty} x ${i.name}`).join(", ")}
                      </div>
                    </td>
                    <td className="adm-num">{money(order.total)}</td>
                    <td><StatusBadge status={order.status} /></td>
                    <td>
                      <StatusSelect order={order} onChange={updateStatus} disabled={updatingId === order.id} />
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => setSelectedOrder(order)}>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ padding: "0 1rem 1.25rem" }}>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </section>

      {/* ORDER DETAILS */}
      {selectedOrder && (
        <div className="adm-overlay" onClick={() => setSelectedOrder(null)}>
          <aside
            className="adm-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={`Order ${selectedOrder.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adm-drawer-head">
              <div>
                <h3>Order #{selectedOrder.id}</h3>
                <p className="adm-muted">{formatDate(selectedOrder.created_at)}</p>
              </div>
              <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
            </div>

            <div className="ui-field">
              <label className="ui-label" htmlFor="drawer-status">Status</label>
              <StatusSelect
                id="drawer-status"
                order={selectedOrder}
                onChange={updateStatus}
                disabled={updatingId === selectedOrder.id}
              />
            </div>

            <h4>Deliver to</h4>
            <p>
              {[selectedOrder.first_name, selectedOrder.last_name].filter(Boolean).join(" ") || "No name given"}
              <br />
              {selectedOrder.address || "No address"}
              <br />
              {[selectedOrder.city, selectedOrder.postcode].filter(Boolean).join(", ")}
            </p>

            <h4>Customer</h4>
            <p>{selectedOrder.email}</p>

            <h4>Items</h4>
            <table className="adm-items">
              <tbody>
                {items(selectedOrder).map((item, i) => (
                  <tr key={i}>
                    <td>{item.name} x {item.qty}</td>
                    <td>{money(Number(item.price) * Number(item.qty))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {Number(selectedOrder.delivery_fee) > 0 && (
                  <tr>
                    <td>Delivery</td>
                    <td>{money(selectedOrder.delivery_fee)}</td>
                  </tr>
                )}
                <tr>
                  <td>Total</td>
                  <td>{money(selectedOrder.total)}</td>
                </tr>
              </tfoot>
            </table>

            {(selectedOrder.paid_at || selectedOrder.stripe_payment_intent) && (
              <>
                <h4>Payment</h4>
                <dl className="adm-kv">
                  {selectedOrder.paid_at && (
                    <>
                      <dt>Paid</dt>
                      <dd>{formatDate(selectedOrder.paid_at)}</dd>
                    </>
                  )}
                  {selectedOrder.stripe_payment_intent && (
                    <>
                      <dt>Stripe reference</dt>
                      <dd className="adm-mono">{selectedOrder.stripe_payment_intent}</dd>
                    </>
                  )}
                </dl>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}